import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import type { NetworkMap, NetworkRouterNode, NetworkServiceNode } from '../api/types';
import { Card, Empty, ErrorAlert, Spinner } from '../components/ui';
import { formatDuration, formatRelative } from '../lib/format';

/*
 * Geometría del diagrama. Tres columnas fijas (panel → router → servicios) y una fila
 * por router; los conectores se dibujan en una capa SVG con curvas Bézier y los nodos
 * son HTML absolutamente posicionado encima, que hereda el tema claro/oscuro gratis.
 */
const ROW_H = 250;
const PANEL = { x: 0, w: 200, h: 96 };
const ROUTER = { x: 305, w: 225, h: 116 };
const SERVICE = { x: 660, w: 240, h: 84 };
const DIAGRAM_W = SERVICE.x + SERVICE.w + 4;

function curve(x1: number, y1: number, x2: number, y2: number): string {
  const mid = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`;
}

const STATUS_TONE: Record<string, string> = {
  ONLINE: 'ok',
  OFFLINE: 'bad',
  AUTH_ERROR: 'bad',
  UNKNOWN: 'neutral',
};

export function NetworkPage() {
  const map = useQuery({
    queryKey: ['network', 'map'],
    queryFn: () => api.get<NetworkMap>('/network/map'),
    // Cada consulta sondea los routers en vivo; 30 s es fresco sin martillearlos.
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const data = map.data;

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Mapa de red</h1>
          <p>
            Topología en vivo: del panel al router por WireGuard, y de ahí a cada servicio con
            sus clientes conectados. Se refresca cada 30 segundos.
          </p>
        </div>
        <button type="button" className="ghost" disabled={map.isFetching} onClick={() => void map.refetch()}>
          {map.isFetching ? 'Sondeando…' : 'Actualizar'}
        </button>
      </div>

      <ErrorAlert error={map.error} />

      {map.isLoading ? (
        <Spinner label="Sondeando la red…" />
      ) : data ? (
        data.routers.length === 0 ? (
          <Card>
            <Empty>No hay routers registrados que dibujar.</Empty>
          </Card>
        ) : (
          <>
            <Diagram data={data} />
            {data.routers.map((router) => (
              <ClientsDetail key={router.id} router={router} />
            ))}
            <p className="faint" style={{ fontSize: 12 }}>
              Sondeado {formatRelative(data.generatedAt)}. Un router en rojo no responde: el resto
              del mapa sigue siendo válido.
            </p>
          </>
        )
      ) : null}
    </main>
  );
}

function Diagram({ data }: { data: NetworkMap }) {
  const height = Math.max(data.routers.length * ROW_H, 280);
  const panelY = height / 2 - PANEL.h / 2;

  return (
    <Card>
      <div className="netmap-scroll">
        <div className="netmap" style={{ width: DIAGRAM_W, height }}>
          <svg className="netmap-lines" width={DIAGRAM_W} height={height} aria-hidden>
            {data.routers.map((router, index) => {
              const rowY = index * ROW_H + ROW_H / 2;
              const tone = router.latencyMs !== null ? 'var(--success)' : 'var(--danger)';
              return (
                <g key={router.id}>
                  {/* panel → router (túnel) */}
                  <path
                    d={curve(PANEL.x + PANEL.w, panelY + PANEL.h / 2, ROUTER.x, rowY)}
                    className="netmap-edge"
                    style={{ stroke: tone }}
                  />
                  {/* router → servicios */}
                  <path
                    d={curve(ROUTER.x + ROUTER.w, rowY, SERVICE.x, rowY - ROW_H / 4)}
                    className="netmap-edge faint-edge"
                  />
                  <path
                    d={curve(ROUTER.x + ROUTER.w, rowY, SERVICE.x, rowY + ROW_H / 4)}
                    className="netmap-edge faint-edge"
                  />
                </g>
              );
            })}
          </svg>

          {/* nodo del panel */}
          <div className="net-node net-panel" style={{ left: PANEL.x, top: panelY, width: PANEL.w }}>
            <div className="net-title">
              <span className="brand-mark" style={{ width: 20, height: 20, fontSize: 11 }}>M</span>
              {data.panel.name}
            </div>
            <div className="faint" style={{ fontSize: 12 }}>{data.panel.host}</div>
            <div className="faint" style={{ fontSize: 11 }}>VPS · túnel WireGuard</div>
          </div>

          {data.routers.map((router, index) => {
            const rowY = index * ROW_H + ROW_H / 2;
            return (
              <RouterNodes key={router.id} router={router} rowY={rowY} panelY={panelY} />
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function RouterNodes({ router, rowY }: { router: NetworkRouterNode; rowY: number; panelY: number }) {
  const tone = STATUS_TONE[router.status] ?? 'neutral';
  return (
    <>
      {/* etiqueta del túnel sobre la línea */}
      <div
        className={`net-edge-label ${router.latencyMs !== null ? '' : 'bad'}`}
        style={{ left: (PANEL.w + ROUTER.x) / 2 - 40, top: (rowY + ROW_H / 8) - ROW_H / 8 - 30 }}
      >
        {router.latencyMs !== null ? `WireGuard · ${router.latencyMs} ms` : 'sin respuesta'}
      </div>

      <div
        className={`net-node net-router ${tone}`}
        style={{ left: ROUTER.x, top: rowY - ROUTER.h / 2, width: ROUTER.w }}
      >
        <div className="net-title">
          <span className={`chip-dot ${tone === 'ok' ? 'ok-dot' : ''}`} />
          {router.identity ?? router.name}
        </div>
        <div className="faint" style={{ fontSize: 12 }}>
          {router.boardName ?? 'MikroTik'} · {router.host}
        </div>
        <div className="faint" style={{ fontSize: 11 }}>
          {router.latencyMs !== null ? (
            <>
              RouterOS {router.version} · CPU {router.cpuLoad ?? '—'}% · up{' '}
              {router.uptimeSeconds ? formatDuration(router.uptimeSeconds) : '—'}
            </>
          ) : (
            router.error ?? 'sin conexión'
          )}
        </div>
      </div>

      <ServiceNode
        node={router.hotspot}
        label="HotSpot"
        top={rowY - ROW_H / 4 - SERVICE.h / 2}
        reachable={router.latencyMs !== null}
      />
      <ServiceNode
        node={router.pppoe}
        label="PPPoE"
        top={rowY + ROW_H / 4 - SERVICE.h / 2}
        reachable={router.latencyMs !== null}
      />
    </>
  );
}

function ServiceNode({
  node,
  label,
  top,
  reachable,
}: {
  node: NetworkServiceNode;
  label: string;
  top: number;
  reachable: boolean;
}) {
  return (
    <div className="net-node net-service" style={{ left: SERVICE.x, top, width: SERVICE.w }}>
      <div className="net-title">
        {label}
        {node.serverName && node.serverName !== label && (
          <span className="faint" style={{ fontWeight: 400 }}> · {node.serverName}</span>
        )}
      </div>
      <div style={{ fontSize: 13 }}>
        {reachable ? (
          <>
            <strong className={node.online > 0 ? 'net-online' : ''}>{node.online}</strong> conectados ·{' '}
            {node.provisioned} dados de alta
          </>
        ) : (
          <span className="faint">{node.provisioned} dados de alta · estado desconocido</span>
        )}
      </div>
      {node.interfaceName && (
        <div className="faint" style={{ fontSize: 11 }}>
          interfaz {node.interfaceName}
        </div>
      )}
    </div>
  );
}

function ClientsDetail({ router }: { router: NetworkRouterNode }) {
  const groups = [
    { label: 'HotSpot', node: router.hotspot },
    { label: 'PPPoE', node: router.pppoe },
  ].filter((group) => group.node.clients.length > 0);

  if (groups.length === 0) return null;

  return (
    <Card title={`Conectados ahora en ${router.identity ?? router.name}`}>
      <div className="card-body" style={{ display: 'grid', gap: 14 }}>
        {groups.map((group) => (
          <div key={group.label}>
            <div className="stat-label" style={{ marginBottom: 8 }}>
              {group.label} · {group.node.clients.length}
            </div>
            <div className="net-clients">
              {group.node.clients.map((client) => (
                <div key={`${group.label}-${client.username}-${client.ipAddress}`} className="net-client">
                  <strong className="mono">{client.username}</strong>
                  <span className="faint">
                    {client.ipAddress ?? 'sin IP'} · {formatDuration(client.uptimeSeconds)}
                  </span>
                  {client.mac && <span className="faint mono" style={{ fontSize: 11 }}>{client.mac}</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
